export const vocalsEnglishContemporarySyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation Level)",
    objective:
      "To establish healthy contemporary vocal technique, basic breath coordination, pitch accuracy, stylistic awareness and confidence in singing English songs.",
    practicalSections: [
      {
        title: "Vocal & body fundamentals",
        items: [
          "Understanding the contemporary singing voice",
          "Natural standing posture",
          "Body relaxation and tension release",
          "Jaw, tongue and neck freedom",
        ],
      },
      {
        title: "Breathing & support",
        items: [
          "Natural diaphragmatic breathing",
          "Breath–voice coordination",
          "Avoidance of breath pushing or throat tension",
        ],
      },
      {
        title: "Tone, pitch & exercises",
        items: [
          "Natural vocal onset with soft, clean attacks",
          "Balanced chest-dominant tone appropriate to style",
          "Major and minor scale patterns and five-note exercises (comfortable keys)",
          "Simple arpeggio and sustained tone exercises",
        ],
      },
      {
        title: "Style, rhythm & repertoire",
        items: [
          "Introduction to pop, soft rock and indie/acoustic styles",
          "Singing with steady tempo and basic rhythmic feels",
          "Two songs: one learner’s choice and one technical song (teacher/examiner guided)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Voice & song structure",
        items: [
          "Basic voice awareness and difference between singing and speaking voice",
          "Melody–lyrics relationship",
          "Verse, chorus and bridge (basic awareness)",
        ],
      },
      {
        title: "Rhythm & vocal health",
        items: [
          "Pulse and tempo and basic rhythmic values (felt, not read)",
          "Hydration, warm-ups, cool-downs and safe practice habits",
        ],
      },
    ],
  },
  2: {
    id: 2,
    title: "Level 2 — Basic Development (Control & Style Awareness)",
    objective:
      "To develop greater vocal control, improved breath management, expanded pitch accuracy and clearer stylistic identity in contemporary English repertoire.",
    practicalSections: [
      {
        title: "Technique, breath & tone",
        items: [
          "Reinforcement of relaxed posture and reduced tension",
          "Controlled diaphragmatic breathing and sustained airflow",
          "Balanced chest and mix voice awareness",
        ],
      },
      {
        title: "Pitch, style & repertoire",
        items: [
          "Pitch stability in stepwise and intervallic patterns",
          "Quarter and eighth-note rhythmic feels and introductory syncopation",
          "Three songs including contrasting styles and one technical song (examiner guided)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Voice technique & song understanding",
        items: [
          "Chest and mix voice concepts and healthy belting awareness (introductory)",
          "Verse–chorus–bridge functions and simple chord movement awareness",
        ],
      },
      {
        title: "Rhythm & health",
        items: [
          "Counting in 4/4 and recognising rhythmic accents",
          "Practice scheduling, early signs of vocal fatigue and recovery strategies",
        ],
      },
    ],
  },
  3: {
    id: 3,
    title: "Level 3 — Intermediate (Range Expansion & Stylistic Confidence)",
    objective:
      "To achieve expanded vocal range, secure mix coordination, expressive flexibility, rhythmic confidence and stylistic maturity in contemporary English singing.",
    practicalSections: [
      {
        title: "Technique, range & tone",
        items: [
          "Stable posture and integrated body movement",
          "Advanced breath coordination for long phrases",
          "Secure mix voice across an expanded range",
          "Tone colour variation for different contemporary genres",
        ],
      },
      {
        title: "Rhythm, style & repertoire",
        items: [
          "Syncopated rhythmic patterns and groove consistency",
          "3–4 songs including technically demanding, stylistically contrasting and expressive ballad repertoire",
        ],
      },
    ],
    theorySections: [
      {
        title: "Voice concepts & musical awareness",
        items: [
          "Mix voice coordination and register blending",
          "Dynamic shaping and song interpretation strategies",
        ],
      },
      {
        title: "Vocal health & longevity",
        items: [
          "Managing increased vocal load and preventing strain",
          "Recovery routines and long-term voice care basics",
        ],
      },
    ],
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 1 (Technical Control & Artistic Expansion)",
    objective:
      "To develop advanced vocal control, secure belt and mix coordination, stylistic versatility, expressive nuance and confident stage performance across contemporary English genres.",
    practicalSections: [
      {
        title: "Advanced technique, breath & tone",
        items: [
          "Complete body freedom during singing with endurance for full-length performances",
          "Advanced breath pacing for long and high-intensity phrases",
          "Secure mix voice and controlled belt (style-appropriate, healthy)",
        ],
      },
      {
        title: "Style, rhythm & repertoire",
        items: [
          "Authenticity in Pop, Rock, R&B and contemporary musical theatre styles",
          "Syncopated and mixed rhythmic patterns and groove stability",
          "4 songs including technically demanding, high-energy, expressive ballad and stylistically contrasting repertoire",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced voice & interpretation",
        items: [
          "Belt mechanics and safety and mix balance strategies",
          "Stylistic phrasing conventions and artistic decision-making",
        ],
      },
      {
        title: "Professional awareness",
        items: [
          "Rehearsal techniques, performance preparation and feedback interpretation",
        ],
      },
    ],
  },
  5: {
    id: 5,
    title: "Level 5 — Professional (Artist Development & Performance Authority)",
    objective:
      "To certify professional-level vocal technique, stylistic authority, artistic individuality and performance readiness for professional contemporary English singing contexts.",
    practicalSections: [
      {
        title: "Technique, range & style",
        items: [
          "Complete physical and vocal freedom with high endurance",
          "Full-range scales and advanced agility/run exercises",
          "Mastery of multiple contemporary genres with authentic phrasing",
        ],
      },
      {
        title: "Rhythm, performance & repertoire",
        items: [
          "Complex rhythmic phrasing and groove leadership",
          "5 songs including a signature song, high-energy piece, expressive ballad, genre-crossing song and optional original composition",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced science, artistry & career", 
        items: [
          "Voice mechanics under high demand and fatigue management",
          "Artistic and branding awareness and professional rehearsal collaboration",
        ],
      },
    ],
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Professional Leadership Level)",
    objective:
      "To certify complete technical freedom, stylistic mastery, artistic authority, creative independence and leadership-level competence in contemporary English vocal performance.",
    practicalSections: [
      {
        title: "Performance & artistry",
        items: [
          "30–45 minute performance set with multi-genre repertoire (pop, rock, R&B, indie, musical pop)",
          "Demonstration of unique artistic identity and advanced emotional communication",
        ],
      },
      {
        title: "Technical mastery & creative skills",
        items: [
          "Effortless control across full range with mastery of mix, belt and textures",
          "Original song performance (mandatory) and advanced improvisation/interpretation",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced vocal science, artistic development & professional practice",
        items: [
          "In-depth vocal mechanics and long-term sustainability",
          "Developing and maintaining a unique vocal identity",
          "Repertoire planning, audition preparation and ethical professional collaboration",
          "Introductory pedagogy and performance psychology for high-level contexts",
        ],
      },
    ],
  },
};
